/**
 * The downsizing plan in one sentence — the SAME sentence on the plan summary rail (Manual Control)
 * and on every Suggestion Engine card, so the two surfaces cannot describe a plan differently.
 *
 * Built ONLY from figures already on the screen beside it, subtracted exactly as the tiles subtract
 * them, and measured against NOT DOWNSIZING — the only honest benchmark for an elective move. The
 * downsizer's own facts lead: what the sale frees (or costs, on a buy-up), where the money goes
 * when it goes into super, and — the thing a client most often misses — that renting is a cost
 * for life and the whole sale is then counted.
 *
 * A lifetime annuity with nothing returned on death is named here, never only in the explainer
 * (owner, 24 Sep 2026: "you're going to get me sued if a client pleads ignorance").
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import {
    pensionClause,
    cashflowClause,
    estateClause,
    plainClause,
    RecapClauses,
    type RecapClause,
} from '../../components/strategy/recapClauses.tsx';

export interface DzInBriefAnnuity {
    amount: number;
    /** 0 = nothing returned on death; 100 = the capital comes back. */
    residualPercent: number;
    /** Whose life the payments are for — "Cameron", or "you". */
    ownerName: string;
}

export interface DzInBriefInput {
    salePrice: number;
    /** What the sale frees after costs, the new home and its duty — 0 on a buy-up or a similar-priced move. */
    freedCapital: number;
    /** What savings had to add to the new home, where it cost more than the sale netted. */
    buyUpShortfall: number;
    sellAndRent: boolean;
    rentPerWeek: number;
    downsizerContribution: number;
    pensionBefore: number;
    pensionAfter: number;
    cashBefore: number;
    cashAfter: number;
    /** The plan's estate at the horizon, and not downsizing's — null when either could not be computed. */
    planLegacy: number | null;
    baseLegacy: number | null;
    legacyYears: number;
    annuity?: DzInBriefAnnuity | null;
}

/** The plain-words fact of a nil-residual annuity, used by the sentence, the card and the recap. */
export function nilResidualAnnuityText(a: DzInBriefAnnuity): string {
    return `the ${formatCurrency(a.amount)} lifetime annuity returns nothing when ${a.ownerName} ${a.ownerName === 'you' ? 'die' : 'dies'} — that money leaves the estate the day it is bought`;
}

export function buildDzInBriefClauses(i: DzInBriefInput): RecapClause[] {
    const clauses: RecapClause[] = [];
    if (i.sellAndRent) {
        clauses.push(plainClause(
            `selling without buying frees the whole ${formatCurrency(i.freedCapital)} after costs${i.downsizerContribution > 0 ? `, of which ${formatCurrency(i.downsizerContribution)} goes into super as a downsizer contribution` : ''}; `
            + (i.rentPerWeek > 0 ? `rent of ${formatCurrency(i.rentPerWeek)} a week is now paid for life, and the whole amount is counted` : 'no rent is entered, so none is charged — and the whole amount is counted'),
        ));
    } else if (i.buyUpShortfall > 0) {
        clauses.push(plainClause(`the new home costs ${formatCurrency(i.buyUpShortfall)} more than the sale leaves, paid from savings — money moves from being counted into the exempt home`));
    } else if (i.freedCapital > 0) {
        clauses.push(plainClause(
            `the move frees up ${formatCurrency(i.freedCapital)}${i.downsizerContribution > 0 ? `, of which ${formatCurrency(i.downsizerContribution)} goes into super as a downsizer contribution` : ''}`,
        ));
    } else if (i.salePrice > 0) {
        clauses.push(plainClause('the move frees up nothing once the new home and the costs are paid — the selling costs are what it costs you'));
    }
    if (i.annuity && i.annuity.amount > 0 && Math.round(i.annuity.residualPercent) === 0) {
        clauses.push({ tone: 'bad', text: nilResidualAnnuityText(i.annuity) });
    }
    /*
     * THE PENSION USUALLY FALLS, AND THAT IS THE POINT OF SAYING IT. The home is exempt; the money
     * it turns into is not. A client who reads only "you free up $600,000" and discovers the
     * pension consequence later has been sold the upside of the decision on its own.
     */
    const pensionDelta = Math.round(i.pensionAfter - i.pensionBefore);
    clauses.push(pensionClause(pensionDelta, pensionDelta < 0 ? ', because the home is exempt and what it turns into is not' : ''));
    const cashflowDelta = Math.round(i.cashAfter - i.cashBefore);
    if (cashflowDelta !== 0) clauses.push(cashflowClause(cashflowDelta, i.cashAfter));
    if (i.planLegacy != null && i.baseLegacy != null) {
        const legacyDelta = i.planLegacy - i.baseLegacy;
        const e = estateClause(legacyDelta, Number(i.legacyYears) || 0);
        clauses.push(Math.round(legacyDelta) === 0 ? e : { ...e, text: e.text.replace(' ahead after', ' ahead of not downsizing after').replace(' behind after', ' behind not downsizing after') });
    }
    return clauses;
}

export function DzInBrief(props: DzInBriefInput) {
    const clauses = buildDzInBriefClauses(props);
    if (!clauses.length) return null;
    return <RecapClauses clauses={clauses} />;
}
